import { ChangeDetectorRef, Component, OnInit, Optional } from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import { MessageService } from 'primeng/api'

import { ApiCustomerVerificationService } from '@modules/private/customer/data/services/api/api-customer-verification.service'
import { CopyBanner, CopyBannerAction, copyBannerOf } from '@modules/private/lease/data/utils/copy-client-verification.util'
import { CopyClientVerificationResult } from '@modules/private/lease/data/interfaces/copy-client-verification.interface'
import { LeaseDetailService } from '@modules/private/lease/data/services/view/lease-detail.service'
import { SnapshotContext } from '@data/interfaces/verification-snapshot.interface'
import { CiecTranslateService } from '@shared/services/ciec-translate.service'

/**
 * Panel "Algoritmo" del detalle del arrendamiento (ADR-0031).
 *
 * Post-refactor (Fase D): este componente queda como una capa delgada que:
 *   - Lee `lease.verification` (snapshot persistido al crear/decidir el lease).
 *   - Calcula si el snapshot está aún editable según el estado del lease.
 *   - Delega TODA la presentación al componente compartido `app-ciec-analysis-view`
 *     (que también renderiza la pestaña "Análisis Fiscal CIEC" del customer-detail).
 *   - Mantiene `runVerification()` que invoca el endpoint existente
 *     `POST /leases/:id/verify-ciec`. Backend ahora valida defensivamente que el
 *     lease no esté en estado terminal antes de re-snapshotear (ConflictException).
 *
 * Estados mutables: `Pendiente | Aprobado` → botón habilitado.
 * Estados congelados: `Activo | Liquidado | Cancelado` → botón deshabilitado con tooltip.
 */
@Component({
  selector: 'app-lease-algorithm',
  templateUrl: './lease-algorithm.component.html',
  styleUrls: ['./lease-algorithm.component.scss'],
  providers: [MessageService],
})
export class LeaseAlgorithmComponent implements OnInit {
  isRunningVerification = false

  //* El análisis de Ekatena que ya existe para el RFC de este cliente, si lo hay. Se busca al
  //* entrar porque preguntarlo no cuesta créditos ni tiempo.
  ekatenaReport: any = null
  ekatenaBuscando = false
  ekatenaImportando = false

  //* B-004 · Si el análisis del arrendamiento vino vacío o no existe: qué se puede hacer (copiar el del cliente,
  //* actualizar datos o solicitar la CIEC). Lo decide el API en seco al entrar y después de cada cambio.
  copyPreview: CopyClientVerificationResult | null = null
  copyBanner: CopyBanner | null = null
  copying = false
  showCiecRequestDialog = false

  private readonly FROZEN_STATES: ReadonlyArray<string> = ['Activo', 'Liquidado', 'Cancelado']
  private snapshotContextCache: SnapshotContext | null = null

  constructor(
    public leaseDetailService: LeaseDetailService,
    private cd: ChangeDetectorRef,
    private messageService: MessageService,
    private readonly apiVerification: ApiCustomerVerificationService,
    private readonly ciecTranslate: CiecTranslateService,
    //* Opcional: fuera del ruteo (pruebas, vista incrustada) simplemente no hay sub-pestaña pedida.
    @Optional() private readonly route?: ActivatedRoute,
  ) {}

  //* Plan v4.3 §9.3 · `?subtab=legal|bureau|analysis|…`: la bandeja de Legal abre la foto en la sección de la señal.
  get initialSubtab(): string | null {
    return this.route?.snapshot?.queryParamMap?.get('subtab') ?? null
  }

  ngOnInit(): void {
    this.buscarReporteEkatena()
    this.loadCopyPreview()
  }

  /** El cliente del arrendamiento (viene poblado en el detalle). */
  get client(): any {
    return (this.leaseDetailService.data as any)?.client ?? null
  }

  get clientId(): string {
    const c = this.client
    return String(c?._id ?? c ?? '')
  }

  get clientName(): string {
    const c = this.client
    return [c?.name, c?.lastName, c?.middleName].filter(Boolean).join(' ') || c?.legalName || ''
  }

  /** Pregunta en seco si hay algo que copiar. Si el API no responde, no hay aviso: no se bloquea nada. */
  loadCopyPreview(): void {
    if (!this.leaseDetailService.id) return
    this.leaseDetailService.copyClientVerification(true).subscribe({
      next: (r) => {
        this.copyPreview = r?.error ? null : (r?.data ?? null)
        this.copyBanner = copyBannerOf(this.copyPreview)
        this.cd.detectChanges()
      },
      error: () => {
        this.copyPreview = null
        this.copyBanner = null
      },
    })
  }

  onCopyBannerAction(action: CopyBannerAction): void {
    if (action === 'copy') this.copyFromClient()
    else if (action === 'refresh') this.runVerification()
    else if (action === 'request') this.showCiecRequestDialog = true
  }

  /** B-004 · Copia el análisis del cliente; el API deja antes → después en la Bitácora. */
  copyFromClient(): void {
    if (this.copying) return
    this.copying = true
    this.leaseDetailService.copyClientVerification(false).subscribe({
      next: (r) => {
        this.copying = false
        if (r?.error || !r?.data) {
          this.messageService.add({ severity: 'warn', summary: 'No se copió el análisis', detail: r?.msg || 'Intenta de nuevo en un momento.', life: 8000 })
          //* Alguien más lo copió primero (o trajo uno nuevo): la pantalla no puede seguir mostrando el vacío.
          if (r?.code === 'LEASE_HAS_ANALYSIS') this.leaseDetailService.refreshVerification()
          this.loadCopyPreview()
          return
        }
        if (this.leaseDetailService.data && r.data.verification) this.leaseDetailService.data.verification = r.data.verification
        this.messageService.add({ severity: 'success', summary: 'Análisis copiado del cliente', detail: 'Quedó registrado en la Bitácora del arrendamiento.' })
        this.loadCopyPreview()
      },
      error: () => {
        this.copying = false
        this.cd.detectChanges()
      },
    })
  }

  /**
   * Averigua si hay un análisis de Ekatena esperando. **No bloquea nada**: si el proveedor no
   * responde, el aviso simplemente no aparece.
   */
  private buscarReporteEkatena(): void {
    const rfc = this.verificationData?.rfc || (this.leaseDetailService.data as any)?.client?.rfc
    if (!rfc) return
    this.ekatenaBuscando = true
    //! El `error` NO es opcional: sin él, una consulta que falla deja `ekatenaBuscando` en true
    //! para siempre y el indicador de búsqueda gira sin fin. Antes no se notaba porque no se
    //! pintaba nada mientras buscaba; ahora sí se pinta.
    this.apiVerification.buscarReporteEkatena(rfc).subscribe({
      next: (r: any) => {
        this.ekatenaBuscando = false
        this.ekatenaReport = r ?? null
        this.cd.detectChanges()
      },
      error: () => {
        this.ekatenaBuscando = false
        this.ekatenaReport = null
        this.cd.detectChanges()
      },
    })
  }

  /**
   * Trae el análisis de Ekatena y vuelve a tomar el snapshot.
   *
   * ! La congelación por estado aplica igual que al verificar: un arrendamiento operativo no
   * ! cambia su análisis por esta vía tampoco. El backend lo valida por su cuenta.
   */
  importarEkatena(): void {
    if (this.ekatenaImportando || !this.canRefreshLeaseSnapshot) return
    this.ekatenaImportando = true
    this.leaseDetailService.importEkatena().subscribe({
      next: (r: any) => {
        this.ekatenaImportando = false
        if (r?.error) {
          this.messageService.add({ severity: 'warn', summary: 'No se pudo importar', detail: r?.msg || 'Intenta de nuevo en un momento.' })
          this.cd.detectChanges()
          return
        }
        if (this.leaseDetailService.data && r?.data?.verification) this.leaseDetailService.data.verification = r.data.verification
        this.messageService.add({ severity: 'success', summary: 'Análisis importado', detail: 'El análisis de Ekatena quedó registrado. El anterior sigue en el historial del cliente.' })
        this.loadCopyPreview()
        this.cd.detectChanges()
      },
      error: () => {
        this.ekatenaImportando = false
        this.cd.detectChanges()
      },
    })
  }

  /** Snapshot del lease — fuente de verdad de la verificación que se muestra. */
  get verificationData(): any | null {
    return this.leaseDetailService.data?.verification ?? null
  }

  /**
   * F-158 · Contexto de la foto de Syntage para las sub-pestañas. Congelado en Activo/Liquidado/Cancelado:
   * ahí no se ofrece el anexo de Buró.
   *
   * ! Se devuelve la MISMA referencia mientras no cambien el arrendamiento ni su congelación: un objeto
   * ! nuevo en cada ciclo de detección cambia el @Input a cada vuelta y Angular lo reporta como
   * ! `ExpressionChangedAfterItHasBeenCheckedError`.
   */
  get snapshotContext(): SnapshotContext | null {
    const rawId = this.leaseDetailService.data?._id
    if (!rawId) return null
    const id = String(rawId)
    const frozen = !this.canRefreshLeaseSnapshot
    const cached = this.snapshotContextCache
    if (cached?.id !== id || cached.frozen !== frozen) this.snapshotContextCache = { scope: 'lease', id, frozen }
    return this.snapshotContextCache
  }

  /** Si el lease está en estado mutable, se permite re-correr CIEC + re-snapshot. */
  get canRefreshLeaseSnapshot(): boolean {
    const status = this.leaseDetailService.data?.status
    return !!status && !this.FROZEN_STATES.includes(status)
  }

  /** Tooltip que explica por qué el botón está deshabilitado. */
  get refreshDisabledReason(): string {
    const status = this.leaseDetailService.data?.status
    if (!status) return ''
    if (this.FROZEN_STATES.includes(status)) {
      //! Sin referencias a decisiones de arquitectura: esto lo lee Operaciones y el Comité, no un
      //! desarrollador. El porqué se explica en términos de negocio; el ADR vive en la nota del
      //! módulo, que es donde alguien lo va a buscar.
      return `El análisis fiscal no se puede volver a correr porque el arrendamiento ya está ${status.toLowerCase()}. A partir de ese momento queda fijo, para que la información con la que se aprobó no cambie después.`
    }
    return ''
  }

  runVerification(): void {
    if (this.isRunningVerification) return
    if (!this.canRefreshLeaseSnapshot) return
    this.isRunningVerification = true
    this.messageService.add({
      severity: 'info',
      summary: 'Procesando',
      detail: 'Ejecutando verificación CIEC, esto puede tardar unos segundos…',
    })

    this.leaseDetailService.runCiecVerification().subscribe({
      next: (r: any) => {
        this.isRunningVerification = false
        if (r?.error) {
          //* B-075: el backend manda el `code` del fallo; el texto y su severidad salen del traductor.
          const info = this.ciecTranslate.describeFailure(r?.code, r?.msg)
          this.messageService.add({ severity: info.severity, summary: info.summary, detail: info.detail, life: 8000 })
          return
        }
        const updatedLease = r?.data
        if (this.leaseDetailService.data && updatedLease?.verification) {
          this.leaseDetailService.data.verification = updatedLease.verification
        }
        this.cd.detectChanges()
        this.loadCopyPreview()
        const ok = this.leaseDetailService.data?.verification?.status === 'success'
        this.messageService.add({
          severity: ok ? 'success' : 'warn',
          summary: ok ? 'Éxito' : 'Atención',
          detail: ok ? 'Verificación CIEC completada' : 'La verificación terminó con estado no exitoso',
        })
      },
      error: (err) => {
        this.isRunningVerification = false
        console.error('Error al ejecutar la verificación CIEC:', err)
        const msg = err?.error?.message || err?.error?.msg || 'No se pudo ejecutar la verificación CIEC'
        this.messageService.add({ severity: 'error', summary: 'Error', detail: msg })
      },
    })
  }
}
